import { FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { ConnectedProps, connect, useDispatch, useSelector } from "react-redux";
import { IGlobalReducerState } from "Store/Base/interface/IGlobalReducerState";
import PageHeader from "Components/UI/PageHeader/PageHeader";
import BreadCrumb, { IBreadcrumbItem } from "Components/UI/BreadCrumb";
import OptionsTabs, { IActiveTab } from "./components/OptionsTabs";
import AppearanceTab from "./components/AppearanceTab";
import LoginScreenTab from "./components/LoginScreenTab";
import useWindowSize from "Hooks/useWindowSize";
import CustomDomainTab from "./components/CustomDomainTab";
import { AuthUtils } from "Utils/AuthUtils";
import PersonalizationCTAUpgrade from "./components/PersonalizationCTAUpgrade";
import { useFeatureFlag } from "Hooks/useFeatureFlag";
import { Mixpanel } from "Utils/MixPanel";
import { getCurrentTenant, getTenants } from "Store/Tenant/Tenant.selector";
import {
  HeaderBreadcrumb,
  HeaderWrapper,
  MainWrapper,
  Wrapper,
} from "./styles";

const Personalization: FC<Props> = ({
  isCollapsed,
  userInfo,
}) => {
  const { t } = useTranslation('personalization');
  const whatsappContactLinkFlag = useFeatureFlag('whatsapp-contact-link');
  const whatsappContactLink = (whatsappContactLinkFlag.enabled && typeof whatsappContactLinkFlag.value === 'string')
    ? whatsappContactLinkFlag.value
    : (process.env.REACT_APP_CONTACT_LINK || '');

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { height } = useWindowSize();
  const tenants = useSelector(getTenants);
  const currentTenant = useSelector(getCurrentTenant);
  const tenantFk = AuthUtils.getLoggedUser()?.currentTenant;

  const tabOptions = useMemo<IActiveTab[]>(() => [
    { id: 'appearance', label: t('tabs.appearance'), icon: 'criativy' },
    { id: 'loginScreen', label: t('tabs.loginScreen'), icon: 'person' },
    { id: 'customDomain', label: t('tabs.customDomain'), icon: 'globe' },
  ], [t]);

  const [activeTab, setActiveTab] = useState<IActiveTab>(tabOptions[0]);

  const breadCrumbs = useMemo(() => {
    const data: IBreadcrumbItem[] = [
      { href: '/config/personalization', description: t('breadcrumbRoot') },
      { href: '/config/personalization', description: t('breadcrumb') },
      { href: '/config/personalization', description: activeTab.label },
    ];
    return data;
  }, [activeTab, t]);

  const handleActiveTab = (tab: IActiveTab) => {
    setActiveTab(tab);
  }

  const upgradePlanRedirect = (role: number, planOrder: number) => {
    Mixpanel.track({
      name: 'CALL_T0_ACTION',
      props: {
        origin: 'personalization',
        originPath: window.location.pathname
      },
      userInfo,
      currentListTenant: currentTenant,
      tenantIds: tenants && tenants.length > 0
        ? tenants?.map((tenant) => tenant?.TenantId).filter((tenantId) => tenantId !== undefined) as number[]
        : undefined,
    });

    if (role === 1) {
      if (planOrder === 4) {
        window.open(whatsappContactLink);
      } else {
        navigate('/faturamento/planos');
      }

    } else {
      window.open('https://bim.maletadoengenheiro.com.br/seja-premium');
    }
  };

  const isAppearanceTab = activeTab.id === 'appearance';
  const isLoginScreen = activeTab.id === 'loginScreen';
  const isCustomDomain = activeTab.id === 'customDomain';
  const isEnterprise = userInfo?.UserStoreItem?.StorePlan?.Order
    && userInfo?.UserStoreItem?.StorePlan?.Order >= 4;

  return (
    <Wrapper>
      <PageHeader
        name={t('pageTitle')}
      >
        <HeaderBreadcrumb>
          <BreadCrumb breadcrumbs={breadCrumbs} />
        </HeaderBreadcrumb>
        {!isEnterprise && (
          <PersonalizationCTAUpgrade
            onConfirm={upgradePlanRedirect}
            order={userInfo?.UserStoreItem.StorePlan?.Order}
            role={userInfo?.CurrentRoleFk}
          />
        )}
        {isEnterprise && (
          <>
            <HeaderWrapper>
              <OptionsTabs
                activeTab={activeTab}
                options={tabOptions}
                onActiveTab={handleActiveTab}
              />
            </HeaderWrapper>
            <MainWrapper isCollapsed={isCollapsed} maxHeightCalc={height}>
              {isAppearanceTab && (
                <AppearanceTab
                  dispatch={dispatch}
                  tenantFk={tenantFk}
                />
              )}
              {isLoginScreen && (
                <LoginScreenTab
                  dispatch={dispatch}
                  tenantFk={tenantFk}
                  onActiveTab={handleActiveTab}
                />
              )}
              {isCustomDomain && (
                <CustomDomainTab
                  dispatch={dispatch}
                  tenantFk={tenantFk}
                />
              )}
            </MainWrapper>
          </>
        )}
      </PageHeader>
    </Wrapper>
  )
}

const mapState = (state: IGlobalReducerState) => ({
  isCollapsed: state.obra.isCollapsed,
  userInfo: state.auth.userInfo,
});

const connector = connect(mapState);

type PropsFromRedux = ConnectedProps<typeof connector>;
type Props = PropsFromRedux;

export default connector(Personalization);
